import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LinkIcon,
  Settings,
  Users,
  UserPlus,
  LogOut,
  BarChart3,
  Sparkles,
  Layers,
  Crown,
  Activity,
  ChartLine,
  QrCode,
  FileText,
  ClipboardList,
  LayoutDashboard,
} from 'lucide-react';
import { isRouteEnabled } from '@/client/lib/config';
import { TrialBanner } from '@/client/components/trial-banner';
import { useAuth } from '@/client/hooks/use-auth';
import { useWorkspace } from '@/client/hooks/use-workspace';
import { signOut } from '@/client/lib/auth-client';
import { WorkspaceSwitcher } from '@/client/components/workspace-switcher';
import { cn } from '@/client/lib/utils';
import { useUIStore } from '@/client/stores/ui-store';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Avatar, AvatarFallback } from '@/client/components/ui/avatar';
import { useFeatures, type FeatureFlag } from '@/client/hooks/use-features';
import { UpgradeModal } from '@/client/components/modals/upgrade-modal';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/client/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

// Simplified navigation: 8 core items only
// Removed: Email Newsletter, Direct Mail, SMS, Workflows (stubs)
// Removed: Campaigns, Shared, Team (consolidating into other features)
// See TSK-017 for rationale
// See TSK-020 for feature gating pattern (always show, open upgrade modal on click)
interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /** Feature flag for gating - if set, non-qualifying users see upgrade modal on click */
  featureFlag?: FeatureFlag;
}

const navigation: NavItem[] = [
  // Overview
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    description: 'Your workspace overview',
  },
  // Create & Manage
  { name: 'Links', href: '/links', icon: LinkIcon, description: 'Shorten and track URLs' },
  { name: 'QR Codes', href: '/qr-codes', icon: QrCode, description: 'Generate scannable codes' },
  { name: 'Pages', href: '/pages', icon: FileText, description: 'Build landing pages' },
  { name: 'Forms', href: '/forms', icon: ClipboardList, description: 'Collect responses' },
  // Insights
  { name: 'Analytics', href: '/analytics', icon: ChartLine, description: 'View detailed insights' },
  // Organize (feature-gated)
  {
    name: 'Projects',
    href: '/projects',
    icon: Layers,
    description: 'Organize your assets',
    featureFlag: 'project_limits', // Plus required to have more than 1 project
  },
  { name: 'Settings', href: '/settings', icon: Settings, description: 'Your preferences' },
];

const adminNavigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: BarChart3, description: 'System overview' },
  { name: 'All Links', href: '/admin/links', icon: LinkIcon, description: 'All user links' },
  { name: 'Users', href: '/admin/users', icon: Users, description: 'User management' },
  {
    name: 'Access Requests',
    href: '/admin/access-requests',
    icon: UserPlus,
    description: 'Pending requests',
  },
  {
    name: 'Audit Logs',
    href: '/admin/audit-logs',
    icon: Activity,
    description: 'Activity history',
  },
];

/**
 * NavMenuItem - Single navigation item with optional feature gating
 *
 * TSK-020: Always shows the item, but opens upgrade modal if user lacks access
 */
function NavMenuItem({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const { hasFeature, getRequiredTier } = useFeatures();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if this item is feature-gated
  const isGated = item.featureFlag && !hasFeature(item.featureFlag);
  const requiredTier = item.featureFlag ? getRequiredTier(item.featureFlag) : 'free';

  const handleClick = (e: React.MouseEvent) => {
    if (isGated) {
      e.preventDefault();
      setShowUpgradeModal(true);
    }
  };

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={item.name}
          className={cn(
            'h-auto py-2.5',
            isActive &&
              '!bg-primary !text-primary-foreground hover:!bg-primary/90 hover:!text-primary-foreground'
          )}
        >
          <Link to={item.href} onClick={handleClick}>
            <div
              className={cn(
                'size-8 rounded-lg flex items-center justify-center shrink-0',
                isActive
                  ? 'bg-white/20 text-primary-foreground'
                  : 'bg-sidebar-accent group-hover/menu-item:bg-primary/10 group-hover/menu-item:text-primary'
              )}
            >
              <item.icon className="size-4" />
            </div>
            <div className="flex-1 overflow-hidden">
              <span className="flex items-center gap-1.5 text-inherit">
                {item.name}
                {isGated && (
                  <Badge
                    variant={requiredTier === 'pro' ? 'secondary' : 'primary-subtle'}
                    className="px-1 py-0 text-[10px] gap-0.5"
                  >
                    <Crown className="size-2.5" />
                    {requiredTier === 'pro' ? 'Pro' : 'Plus'}
                  </Badge>
                )}
              </span>
              <span
                className={cn(
                  'text-xs truncate block',
                  isActive ? '!text-primary-foreground/70' : 'text-muted-foreground'
                )}
              >
                {item.description}
              </span>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>

      {item.featureFlag && (
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          feature={item.featureFlag}
        />
      )}
    </>
  );
}

function AppSidebar() {
  const { user, isAdmin, isPlusUser } = useAuth();
  const { hasMultipleWorkspaces } = useWorkspace();
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <Sidebar collapsible="icon" className="vt-sidebar border-r border-sidebar-border">
      {/* Logo */}
      <SidebarHeader className="h-16 border-b border-sidebar-border px-4 flex items-center justify-start">
        <Link to="/" className="flex items-center gap-3 group h-full py-3">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl blur-sm group-hover:blur-md transition-all" />
            <img
              src="/assets/logo.png"
              alt="Armenian Link"
              className="relative size-9 rounded-xl group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          {!isCollapsed && (
            <span className="font-semibold text-lg tracking-tight leading-none">Armenian Link</span>
          )}
        </Link>
      </SidebarHeader>

      {/* Workspace Switcher - shown when user has multiple workspaces */}
      {hasMultipleWorkspaces && (
        <div className="px-4 py-3 border-b border-sidebar-border">
          <WorkspaceSwitcher />
        </div>
      )}

      <SidebarContent className="py-4">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigation
                .filter((item) => isRouteEnabled(item.href))
                .map((item) => {
                  const isActive = location.pathname === item.href;
                  return <NavMenuItem key={item.name} item={item} isActive={isActive} />;
                })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin section */}
        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="gap-2">
              <Sparkles className="size-3.5 text-primary" />
              Admin Tools
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.name}
                        className={cn(
                          'h-auto py-2.5',
                          isActive &&
                            '!bg-[hsl(262,83%,58%)] !text-white hover:!bg-[hsl(262,83%,55%)] hover:!text-white'
                        )}
                      >
                        <Link to={item.href}>
                          <div
                            className={cn(
                              'size-8 rounded-lg flex items-center justify-center shrink-0',
                              isActive
                                ? 'bg-white/20 text-white'
                                : 'bg-sidebar-accent group-hover/menu-item:bg-[hsl(262,83%,58%)]/10 group-hover/menu-item:text-[hsl(262,83%,58%)]'
                            )}
                          >
                            <item.icon className="size-4" />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <span className="block text-inherit">{item.name}</span>
                            <span
                              className={cn(
                                'text-xs truncate block',
                                isActive ? '!text-white/70' : 'text-muted-foreground'
                              )}
                            >
                              {item.description}
                            </span>
                          </div>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* User Footer */}
      <SidebarFooter className="border-t border-sidebar-border p-3">
        <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-sidebar-accent transition-colors group">
          <div className="relative shrink-0">
            <Avatar className="size-10 rounded-xl">
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-sm font-bold rounded-xl">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-success border-2 border-sidebar" />
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  {isPlusUser && (
                    <Badge
                      variant="primary-subtle"
                      className="px-1.5 py-0.5 text-[10px] font-bold gap-0.5"
                    >
                      <Crown className="size-2.5" />
                      PLUS
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleSignOut}
                    className="opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                    aria-label="Sign out"
                  >
                    <LogOut className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Sign out</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function AppHeader() {
  const { user, isAdmin, isPlusUser } = useAuth();

  return (
    <header className="vt-header sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 border-b border-border bg-card px-4 lg:px-6 safe-area-x">
      <SidebarTrigger className="md:hidden" />

      {/* Mobile logo */}
      <Link to="/" className="md:hidden flex items-center gap-2">
        <img src="/assets/logo.png" alt="Armenian Link" className="size-8 rounded-lg" />
        <span className="font-bold">Armenian Link</span>
      </Link>

      <div className="flex-1" />

      {/* User info for desktop */}
      <div className="hidden md:flex items-center gap-3">
        {isPlusUser && (
          <Badge variant="primary-subtle" className="gap-1.5">
            <Crown className="size-3" />
            Plus
          </Badge>
        )}
        {isAdmin && (
          <Badge className="gap-1.5 bg-[hsl(262,83%,58%)]/10 text-[hsl(262,83%,58%)] border-0">
            <Sparkles className="size-3" />
            Admin
          </Badge>
        )}
        <div className="h-8 w-px bg-border" />
        <span className="text-sm text-muted-foreground">{user?.email}</span>
      </div>
    </header>
  );
}

export function AppLayout() {
  const { openCreateLinkModal } = useUIStore();

  // Global keyboard shortcut: Cmd/Ctrl+K to open quick create modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openCreateLinkModal();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openCreateLinkModal]);

  return (
    <SidebarProvider>
      {/* CSS Grid macro-layout: sidebar + main content area */}
      <div className="grid-app-shell">
        <AppSidebar />
        <SidebarInset className="grid-main-content bg-gradient-to-br from-background via-background to-muted/20 overflow-hidden">
          <AppHeader />
          <TrialBanner />
          {/* Main content area with view transition and proper overflow */}
          <main className="vt-main overflow-y-auto overflow-x-hidden section-padding">
            <div className="content-width">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
